import { angleBetween, cross, dot, normalise, scale, sub, vec } from './vec3.js';
const DEG = Math.PI / 180;
export function toVec3({ lat, lon }) {
    const phi = lat * DEG;
    const lambda = lon * DEG;
    const cosPhi = Math.cos(phi);
    return vec(cosPhi * Math.cos(lambda), Math.sin(phi), cosPhi * Math.sin(lambda));
}
export function toLatLon(v) {
    const n = normalise(v);
    return {
        lat: Math.asin(Math.max(-1, Math.min(1, n.y))) / DEG,
        lon: Math.atan2(n.z, n.x) / DEG,
    };
}
/** Geodesic distance in radians on the unit sphere. */
export function geodesic(a, b) {
    return angleBetween(toVec3(a), toVec3(b));
}
/** Area of a spherical polygon in steradians, always positive. */
export function polygonArea(ring) {
    const n = ring.length;
    if (n < 3)
        return 0;
    // L'Huilier-free approach: sum the signed areas of triangles from a fixed
    // reference point, which is exact on the sphere and robust for our sizes.
    const origin = ring[0];
    let total = 0;
    for (let i = 1; i < n - 1; i += 1) {
        total += sphericalTriangleArea(origin, ring[i], ring[i + 1]);
    }
    return Math.abs(total);
}
/** Signed area of a spherical triangle of unit vectors, in steradians. */
export function sphericalTriangleArea(a, b, c) {
    // Van Oosterom & Strackee: tan(E/4) = |a·(b×c)| / (1 + a·b + b·c + c·a)
    const numerator = dot(a, cross(b, c));
    const denominator = 1 + dot(a, b) + dot(b, c) + dot(c, a);
    return 2 * Math.atan2(numerator, denominator);
}
/**
 * The signed turning angle of a ring as seen from `point`.
 *
 * On a sphere a closed ring always subtends 2π from either side — there is no
 * "far away" — so the magnitude alone cannot tell inside from outside. The sign
 * can: it is positive on the side the ring encircles counter-clockwise.
 */
export function windingAt(ring, point) {
    const n = ring.length;
    if (n < 3)
        return 0;
    let winding = 0;
    for (let i = 0; i < n; i += 1) {
        const a = ring[i];
        const b = ring[(i + 1) % n];
        const ta = normalise(sub(a, scale(point, dot(a, point))));
        const tb = normalise(sub(b, scale(point, dot(b, point))));
        winding += Math.atan2(dot(cross(ta, tb), point), dot(ta, tb));
    }
    return winding;
}
/**
 * Point-in-polygon on the sphere, correct across the antimeridian and at the
 * poles where a lat/lon test is not, and correct for the antipode where a
 * magnitude-only winding test is not.
 */
export function containsPoint(ring, point) {
    const winding = windingAt(ring, point);
    if (Math.abs(winding) < 1e-6)
        return false;
    return winding > 0 === isCounterClockwise(ring);
}
/** Whether a ring is wound counter-clockwise when seen from outside. */
export function isCounterClockwise(ring) {
    const n = ring.length;
    if (n < 3)
        return false;
    let total = 0;
    const origin = ring[0];
    for (let i = 1; i < n - 1; i += 1) {
        total += sphericalTriangleArea(origin, ring[i], ring[i + 1]);
    }
    return total > 0;
}
/** The area of a spherical cap of the given angular radius, for test anchors. */
export function capArea(angularRadius) {
    return 2 * Math.PI * (1 - Math.cos(angularRadius));
}
//# sourceMappingURL=sphere.js.map